import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

import TimelineIcon from '@mui/icons-material/Timeline';
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import ScheduleIcon from '@mui/icons-material/Schedule';
import ShareLocationIcon from '@mui/icons-material/ShareLocation';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import DescriptionIcon from '@mui/icons-material/Description';
import BoltIcon from '@mui/icons-material/Bolt';
import WifiOffIcon from '@mui/icons-material/WifiOff';
import LockIcon from '@mui/icons-material/Lock';

import { TOOLS } from './Home';
import ContactForm from '../components/ContactForm';
import { TagTailBanner, TagTailNavLink } from './TagTail'; // TagTail promo — see TagTail.js to remove

// App Store listing for the LE Tech Tools iOS app.
const IOS_APP_URL = 'https://apps.apple.com/app/id6788892508';

// ── Palette (dark cinematic) ──────────────────────────────────────────────────
const INK = '#0a0b16';
const INDIGO = '#6366f1';
const VIOLET = '#a855f7';
const TEXT = '#e5e7f0';
const MUTED = '#9aa3b8';
const CARD = 'rgba(255,255,255,0.04)';
const CARD_BORDER = 'rgba(255,255,255,0.09)';

// Count-up animation for the stat row, triggered when it scrolls into view.
function useCountUp(target, run, duration = 1400) {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!run) return;
    let raf;
    const start = performance.now();
    const tick = now => {
      const p = Math.min((now - start) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setVal(Math.round(eased * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, run, duration]);
  return val;
}

// Fires once when the element first enters the viewport.
function useInView() {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } },
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
}

const FEATURES = [
  { icon: TimelineIcon, color: '#34d399',
    title: 'LPR pattern analysis',
    desc: 'Turn OpenALPR reads into movement patterns, hotspots, hourly trends, and next-camera prediction for a vehicle.' },
  { icon: ManageSearchIcon, color: '#818cf8',
    title: 'Legal-process return parsers',
    desc: 'Drop in Facebook, Instagram, Snapchat, Google, or Apple warrant returns and browse accounts, messages, IPs, and media.' },
  { icon: ScheduleIcon, color: '#f472b6',
    title: 'Time & video converters',
    desc: 'Fix an unsynced DVR clock and convert UTC timestamps to local time in seconds — no spreadsheet math.' },
  { icon: ShareLocationIcon, color: '#60a5fa',
    title: 'Live video canvass',
    desc: 'Share one map so a team pulling surveillance video tracks every address, hit, and direction in real time.' },
  { icon: ImageSearchIcon, color: '#2dd4bf',
    title: 'EXIF & metadata',
    desc: 'Extract GPS coordinates, device info, and timestamps from photos — JPEG, HEIC, PNG, and more.' },
  { icon: DescriptionIcon, color: '#fbbf24',
    title: 'Case paperwork',
    desc: 'Generate preservation requests and provider-specific subpoena riders, ready to download.' },
];

const STEPS = [
  { n: '1', title: 'Open a tool', desc: 'Pick from the dashboard — no account, no install, no license.' },
  { n: '2', title: 'Drop in your file', desc: 'Your CSV, ZIP, or photo is parsed right on your device. Nothing is uploaded.' },
  { n: '3', title: 'Work offline', desc: 'Once the page loads you can disable WiFi. The results are yours alone.' },
];

const FAQS = [
  { q: 'Is my data private?',
    a: 'Yes. Every tool runs 100% in your browser. No uploaded file, plate, image, or case data is ever sent to a server. Once the page loads you can disable WiFi and the tools still work. Three tools are labeled exceptions, because they cannot work any other way: the Video Canvass Tracker and Area Search Coordinator share a live map with your team, and IP and Domain Intelligence looks up IP addresses and domains online — though its IP blocklist screening still runs entirely in your browser.' },
  { q: 'Are these tools free to use?',
    a: 'Yes. LE Tech Tools is free to use in any modern web browser — no account, install, or license required.' },
  { q: 'What can these tools do?',
    a: 'Analyze OpenALPR license-plate-reader data, parse Facebook, Instagram, Snapchat, Google, and Apple legal-process returns, convert DVR and UTC times, read photo EXIF metadata, generate preservation requests and subpoena riders, and run a shared video canvass.' },
];

function PrimaryCTA({ children, style, className = '' }) {
  return (
    <Link to="/tools" className={`launch ${className}`} style={{
      display: 'inline-flex', alignItems: 'center', gap: 8, textDecoration: 'none',
      padding: '14px 28px', borderRadius: 12, fontSize: 15, fontWeight: 700, color: '#fff',
      background: `linear-gradient(135deg, ${INDIGO}, ${VIOLET})`,
      boxShadow: `0 8px 30px rgba(99,102,241,0.45)`,
      ...style,
    }}>
      {children}
    </Link>
  );
}

function StatRow() {
  const [ref, inView] = useInView();
  const tools = useCountUp(TOOLS.length, inView);
  const pct = useCountUp(100, inView);
  const stats = [
    { display: `${tools}`, label: 'Investigator tools' },
    { display: '0', label: 'Files sent to a server' },
    { display: `${pct}%`, label: 'Runs in your browser' },
  ];
  return (
    <div ref={ref} style={{
      display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 16,
      maxWidth: 760, margin: '44px auto 0',
    }}>
      {stats.map(s => (
        <div key={s.label} style={{
          textAlign: 'center', padding: '22px 12px', borderRadius: 16,
          background: CARD, border: `1px solid ${CARD_BORDER}`,
        }}>
          <div style={{
            fontSize: 'clamp(28px, 5vw, 40px)', fontWeight: 800, letterSpacing: '-1px',
            background: `linear-gradient(135deg, ${TEXT}, ${INDIGO})`,
            WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
          }}>
            {s.display}
          </div>
          <div style={{ fontSize: 12.5, color: MUTED, marginTop: 6, lineHeight: 1.4 }}>{s.label}</div>
        </div>
      ))}
    </div>
  );
}

function Landing() {
  return (
    <div style={{
      background: INK, color: TEXT, minHeight: '100vh',
      fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      overflowX: 'hidden',
    }}>
      <style>{`
        @keyframes floatGlow { 0%,100% { transform: translate(0,0); opacity:.75 } 50% { transform: translate(0,-24px); opacity:1 } }
        @keyframes fadeUp { from { opacity:0; transform: translateY(16px) } to { opacity:1; transform: translateY(0) } }
        .fade-up { animation: fadeUp .7s ease both; }
        .feat-card { transition: transform .18s ease, border-color .18s ease, background .18s ease; }
        .feat-card:hover { transform: translateY(-4px); border-color: rgba(129,140,248,.55); background: rgba(255,255,255,.06); }
        a.launch { transition: transform .15s ease, filter .15s ease; }
        a.launch:hover { filter: brightness(1.1); transform: translateY(-2px); }
        @keyframes ctaPulse {
          0%, 100% { box-shadow: 0 8px 30px rgba(99,102,241,.45); }
          50% { box-shadow: 0 12px 46px rgba(139,92,246,.8); }
        }
        .cta-hero { animation: ctaPulse 2.2s ease-in-out infinite; }
        .nav-bar a, .nav-brand { white-space: nowrap; }
        @media (max-width: 480px) {
          .nav-bar { padding: 14px 16px !important; }
          .nav-links { gap: 12px !important; }
          .nav-brand-text { display: none; }
        }
      `}</style>

      {/* Nav */}
      <nav className="nav-bar" style={{
        position: 'sticky', top: 0, zIndex: 50,
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        maxWidth: 1200, margin: '0 auto', padding: '18px 32px',
        backdropFilter: 'blur(6px)',
      }}>
        <div className="nav-brand" style={{ display: 'flex', alignItems: 'center', gap: 10, fontWeight: 800, fontSize: 17, letterSpacing: '-0.3px' }}>
          <span style={{
            display: 'inline-flex', width: 30, height: 30, borderRadius: 8,
            background: `linear-gradient(135deg, ${INDIGO}, ${VIOLET})`,
            alignItems: 'center', justifyContent: 'center',
          }}>
            <LockIcon style={{ fontSize: 17, color: '#fff' }} />
          </span>
          <span className="nav-brand-text">LE Tech Tools</span>
        </div>
        <div className="nav-links" style={{ display: 'flex', alignItems: 'center', gap: 18 }}>
          <Link to="/guides" style={{ textDecoration: 'none', color: MUTED, fontSize: 14, fontWeight: 600 }}>
            Guides
          </Link>
          <TagTailNavLink />{/* TagTail promo */}
          <Link className="launch" to="/tools" style={{
            textDecoration: 'none', color: TEXT, fontSize: 14, fontWeight: 600,
            padding: '9px 18px', borderRadius: 10, border: `1px solid ${CARD_BORDER}`, background: CARD,
          }}>
            Launch tools →
          </Link>
        </div>
      </nav>

      {/* Hero */}
      <header style={{ position: 'relative', maxWidth: 1200, margin: '0 auto', padding: '72px 32px 40px', textAlign: 'center' }}>
        {/* Glows */}
        <div aria-hidden style={{ position: 'absolute', top: -40, left: '18%', width: 320, height: 320, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(99,102,241,.35), transparent 65%)', filter: 'blur(20px)', animation: 'floatGlow 9s ease-in-out infinite', pointerEvents: 'none' }} />
        <div aria-hidden style={{ position: 'absolute', top: 20, right: '14%', width: 300, height: 300, borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(168,85,247,.30), transparent 65%)', filter: 'blur(20px)', animation: 'floatGlow 11s ease-in-out infinite', pointerEvents: 'none' }} />

        <div className="fade-up" style={{ position: 'relative' }}>
          <div style={{
            display: 'inline-flex', alignItems: 'center', gap: 8, marginBottom: 22,
            fontSize: 12, fontWeight: 700, letterSpacing: '.06em', textTransform: 'uppercase',
            padding: '6px 14px', borderRadius: 20, color: '#c7d2fe',
            background: 'rgba(99,102,241,.14)', border: '1px solid rgba(99,102,241,.35)',
          }}>
            <BoltIcon style={{ fontSize: 15 }} /> Built for law enforcement
          </div>

          <h1 style={{
            fontSize: 'clamp(38px, 6.5vw, 68px)', fontWeight: 800, lineHeight: 1.05, letterSpacing: '-1.5px',
            margin: '0 auto 20px', maxWidth: 900,
          }}>
            Investigator tools that{' '}
            <span style={{
              background: `linear-gradient(120deg, ${INDIGO}, ${VIOLET})`,
              WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text',
            }}>
              never leave your browser
            </span>
          </h1>

          <p style={{ fontSize: 'clamp(15px, 2.2vw, 19px)', color: MUTED, maxWidth: 640, margin: '0 auto 34px', lineHeight: 1.6 }}>
            {TOOLS.length} free tools for LPR analysis, warrant-return parsing, time &amp; video conversion,
            and field operations — all running 100% locally. No uploads. No accounts. No data ever sent to a server.
          </p>

          <div style={{ display: 'flex', justifyContent: 'center' }}>
            <PrimaryCTA className="cta-hero" style={{ padding: '20px 46px', fontSize: 19, borderRadius: 14 }}>
              Launch the tools →
            </PrimaryCTA>
          </div>

          <StatRow />
        </div>
      </header>

      {/* Privacy band */}
      <section style={{ maxWidth: 1000, margin: '20px auto 0', padding: '0 32px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', justifyContent: 'center',
          padding: '20px 26px', borderRadius: 16, textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(52,211,153,.10), rgba(99,102,241,.08))',
          border: '1px solid rgba(52,211,153,.28)',
        }}>
          <WifiOffIcon style={{ fontSize: 26, color: '#34d399' }} />
          <span style={{ fontSize: 15, color: TEXT, lineHeight: 1.5 }}>
            <strong>Disable your WiFi after the page loads</strong> — every tool keeps working. That's the whole point:
            your case data stays on your machine.{' '}
            <Link to="/code-validation" style={{ color: '#6ee7b7', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>
              See how we verify this →
            </Link>
          </span>
        </div>
      </section>

      {/* iOS app callout */}
      <section style={{ maxWidth: 1000, margin: '28px auto 0', padding: '0 32px' }}>
        <div style={{
          display: 'flex', alignItems: 'center', gap: 26, flexWrap: 'wrap', justifyContent: 'center',
          padding: '30px 32px', borderRadius: 20, textAlign: 'center',
          background: 'linear-gradient(135deg, rgba(99,102,241,.16), rgba(168,85,247,.12))',
          border: '1px solid rgba(129,140,248,.35)',
        }}>
          <div style={{
            width: 60, height: 60, borderRadius: 15, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'linear-gradient(135deg, #1f2937, #0a0b16)', border: '1px solid rgba(255,255,255,.12)',
          }}>
            <svg width="32" height="32" viewBox="0 0 384 512" fill="#fff" aria-hidden>
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
            </svg>
          </div>
          <div style={{ textAlign: 'left', maxWidth: 460 }}>
            <div style={{ fontSize: 12, fontWeight: 700, letterSpacing: '.08em', textTransform: 'uppercase', color: '#c7d2fe', marginBottom: 6 }}>
              Now on iPhone &amp; iPad
            </div>
            <h2 style={{ fontSize: 'clamp(20px, 3vw, 26px)', fontWeight: 800, letterSpacing: '-0.5px', margin: '0 0 8px', color: TEXT }}>
              Get the LE Tech Tools iOS app
            </h2>
            <p style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.6, margin: 0 }}>
              Take the toolkit into the field — the same private, browser-based tools, now a tap away on your device.
            </p>
          </div>
          <a href={IOS_APP_URL} target="_blank" rel="noopener noreferrer" className="launch" style={{
            display: 'inline-flex', alignItems: 'center', gap: 10, textDecoration: 'none', flexShrink: 0,
            padding: '12px 22px', borderRadius: 12, background: '#000', border: '1px solid rgba(255,255,255,.18)',
          }}>
            <svg width="24" height="24" viewBox="0 0 384 512" fill="#fff" aria-hidden>
              <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"/>
            </svg>
            <span style={{ textAlign: 'left', lineHeight: 1.1 }}>
              <span style={{ display: 'block', fontSize: 10, color: '#d1d5db' }}>Download on the</span>
              <span style={{ display: 'block', fontSize: 17, fontWeight: 700, color: '#fff' }}>App Store</span>
            </span>
          </a>
        </div>
      </section>

      {/* TagTail promo */}
      <section style={{ maxWidth: 1000, margin: '28px auto 0', padding: '0 32px' }}>
        <TagTailBanner />
      </section>

      {/* Features */}
      <section style={{ maxWidth: 1200, margin: '0 auto', padding: '72px 32px 20px' }}>
        <h2 style={{ fontSize: 'clamp(26px, 4vw, 38px)', fontWeight: 800, letterSpacing: '-0.8px', textAlign: 'center', margin: '0 0 10px' }}>
          Everything an investigation needs
        </h2>
        <p style={{ fontSize: 16, color: MUTED, textAlign: 'center', maxWidth: 560, margin: '0 auto 44px', lineHeight: 1.6 }}>
          Purpose-built utilities that replace tedious manual work — each one self-contained and private.
        </p>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: 20 }}>
          {FEATURES.map(f => {
            const Icon = f.icon;
            return (
              <div key={f.title} className="feat-card" style={{
                padding: '26px 24px', borderRadius: 16,
                background: CARD, border: `1px solid ${CARD_BORDER}`,
              }}>
                <div style={{
                  width: 48, height: 48, borderRadius: 12, marginBottom: 16,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: `${f.color}1f`, border: `1px solid ${f.color}44`,
                }}>
                  <Icon style={{ fontSize: 26, color: f.color }} />
                </div>
                <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 8px', color: TEXT }}>{f.title}</h3>
                <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.65, margin: 0 }}>{f.desc}</p>
              </div>
            );
          })}
        </div>
      </section>

      {/* How it works */}
      <section id="how" style={{ maxWidth: 1100, margin: '0 auto', padding: '72px 32px 20px' }}>
        <h2 style={{ fontSize: 'clamp(26px, 4vw, 38px)', fontWeight: 800, letterSpacing: '-0.8px', textAlign: 'center', margin: '0 0 44px' }}>
          How it works
        </h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 22 }}>
          {STEPS.map(s => (
            <div key={s.n} style={{ padding: '28px 24px', borderRadius: 16, background: CARD, border: `1px solid ${CARD_BORDER}` }}>
              <div style={{
                width: 42, height: 42, borderRadius: '50%', marginBottom: 16,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18, fontWeight: 800, color: '#fff',
                background: `linear-gradient(135deg, ${INDIGO}, ${VIOLET})`,
              }}>
                {s.n}
              </div>
              <h3 style={{ fontSize: 17, fontWeight: 700, margin: '0 0 8px' }}>{s.title}</h3>
              <p style={{ fontSize: 14, color: MUTED, lineHeight: 1.65, margin: 0 }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQ */}
      <section style={{ maxWidth: 860, margin: '0 auto', padding: '72px 32px 20px' }}>
        <h2 style={{ fontSize: 'clamp(26px, 4vw, 38px)', fontWeight: 800, letterSpacing: '-0.8px', textAlign: 'center', margin: '0 0 40px' }}>
          Frequently asked questions
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {FAQS.map(f => (
            <div key={f.q} style={{ padding: '22px 24px', borderRadius: 14, background: CARD, border: `1px solid ${CARD_BORDER}` }}>
              <h3 style={{ fontSize: 16, fontWeight: 700, margin: '0 0 8px', color: TEXT }}>{f.q}</h3>
              <p style={{ fontSize: 14.5, color: MUTED, lineHeight: 1.65, margin: 0 }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section style={{ maxWidth: 1100, margin: '0 auto', padding: '64px 32px 80px' }}>
        <div style={{
          position: 'relative', overflow: 'hidden', textAlign: 'center',
          padding: '56px 32px', borderRadius: 24,
          background: 'linear-gradient(135deg, rgba(99,102,241,.18), rgba(168,85,247,.14))',
          border: '1px solid rgba(129,140,248,.35)',
        }}>
          <h2 style={{ fontSize: 'clamp(26px, 4vw, 40px)', fontWeight: 800, letterSpacing: '-0.8px', margin: '0 0 14px' }}>
            Ready when you are
          </h2>
          <p style={{ fontSize: 16, color: MUTED, maxWidth: 520, margin: '0 auto 30px', lineHeight: 1.6 }}>
            Jump straight into {TOOLS.length} tools built to make investigative work faster — and keep it private.
          </p>
          <PrimaryCTA style={{ padding: '16px 34px', fontSize: 16 }}>Launch the tools →</PrimaryCTA>
        </div>
      </section>

      {/* Contact */}
      <ContactForm />

      {/* Footer */}
      <footer style={{ borderTop: `1px solid ${CARD_BORDER}` }}>
        <div style={{ maxWidth: 1200, margin: '0 auto', padding: '30px 32px', textAlign: 'center' }}>
          <p style={{ fontSize: 13.5, color: MUTED, margin: '0 0 6px', lineHeight: 1.6 }}>
            <strong style={{ color: TEXT }}>LE Tech Tools</strong> — free, browser-based tools for law enforcement and
            investigators. LPR analysis, warrant-return parsers, time &amp; video converters, EXIF metadata, and more.
          </p>
          <p style={{ fontSize: 12.5, color: '#6b7280', margin: '0 0 14px' }}>
            100% local &amp; private — no case data is ever sent to a server.
          </p>
          <div style={{ display: 'flex', gap: 20, justifyContent: 'center', flexWrap: 'wrap', fontSize: 13, fontWeight: 600 }}>
            <Link to="/tools" style={{ color: MUTED, textDecoration: 'none' }}>All tools</Link>
            <Link to="/guides" style={{ color: MUTED, textDecoration: 'none' }}>Guides</Link>
            <a href={IOS_APP_URL} target="_blank" rel="noopener noreferrer" style={{ color: MUTED, textDecoration: 'none' }}>iOS app</a>
            <a href="#contact" style={{ color: MUTED, textDecoration: 'none' }}>Contact</a>
            <Link to="/code-validation" style={{ color: MUTED, textDecoration: 'none' }}>Privacy verified</Link>
            <Link to="/privacy" style={{ color: MUTED, textDecoration: 'none' }}>Privacy Policy</Link>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Landing;
